import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Grouped category selector with custom text input fallback.
 * Fetches categories from /api/event-categories/grouped on mount.
 * 
 * Props:
 *  - value: string (selected category id or custom text)
 *  - onChange: (value: string, label: string) => void
 *  - placeholder: string
 *  - required: boolean - if true, visual indicator shown
 *  - showExamples: boolean - show example text under each option
 *  - allowCustom: boolean - show "Other" text input option
 *  - size: "sm" | "default" 
 *  - className: string
 *  - dataTestId: string
 */
export default function CategorySelector({
  value = '',
  onChange,
  placeholder = 'Select a category',
  required = false,
  showExamples = true,
  allowCustom = true,
  size = 'default',
  className = '',
  dataTestId = 'category-selector',
}) {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/event-categories/grouped`)
      .then(r => r.json())
      .then(data => setGroups(data))
      .catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find selected label
  const getLabel = () => {
    if (!value) return '';
    for (const g of groups) {
      const cat = g.categories.find(c => c.id === value);
      if (cat) return cat.name;
    }
    // Custom or legacy value — show as-is with title case
    return value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const filteredGroups = groups.map(g => ({
    ...g,
    categories: g.categories.filter(c =>
      !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.example && c.example.toLowerCase().includes(search.toLowerCase()))
    ),
  })).filter(g => g.categories.length > 0);

  const handleSelect = (cat) => {
    onChange(cat.id, cat.name);
    setOpen(false);
    setSearch('');
    setCustomMode(false);
  };

  const handleCustomSubmit = () => {
    if (customText.trim()) {
      const id = customText.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      onChange(id, customText.trim());
      setOpen(false);
      setCustomMode(false);
      setCustomText('');
      setSearch('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', '');
    setCustomMode(false);
    setCustomText('');
  };

  const isSmall = size === 'sm';
  const label = getLabel();

  return (
    <div className={`relative ${className}`} ref={dropdownRef} data-testid={dataTestId}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => { setOpen(!open); if (!open) setTimeout(() => inputRef.current?.focus(), 100); }}
        className={`w-full flex items-center justify-between rounded-md border border-slate-200 bg-white text-left transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
          isSmall ? 'h-9 px-3 text-xs' : 'h-10 px-3 text-sm'
        } ${!label ? 'text-slate-400' : 'text-slate-900'}`}
        data-testid={`${dataTestId}-trigger`}
      >
        <span className="truncate flex-1">
          {label || placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {value && (
            <span
              onClick={handleClear}
              className="p-0.5 hover:bg-slate-100 rounded cursor-pointer"
              data-testid={`${dataTestId}-clear`}
            >
              <X className="h-3 w-3 text-slate-400" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[280px] max-h-[380px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${dataTestId}-dropdown`}>
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search categories..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid={`${dataTestId}-search`}
              />
            </div>
          </div>

          {/* Category Groups */}
          <div className="overflow-y-auto max-h-[300px] p-1">
            {filteredGroups.map((group) => (
              <div key={group.group_id} className="mb-1">
                <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {group.group_name}
                </div>
                {group.categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleSelect(cat)}
                    className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                      value === cat.id
                        ? 'bg-emerald-50 text-emerald-700 font-medium'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                    data-testid={`${dataTestId}-option-${cat.id}`}
                  >
                    <span className="block">{cat.name}</span>
                    {showExamples && cat.example && (
                      <span className="block text-[10px] text-slate-400 mt-0.5">{cat.example}</span>
                    )}
                  </button>
                ))}
              </div>
            ))}

            {filteredGroups.length === 0 && !customMode && (
              <div className="px-3 py-4 text-center text-sm text-slate-400">
                No categories match "{search}"
              </div>
            )}

            {/* Custom Input Option */}
            {allowCustom && (
              <div className="border-t border-slate-100 mt-1 pt-1 px-2 pb-2">
                {!customMode ? (
                  <button
                    type="button"
                    onClick={() => { setCustomMode(true); setCustomText(search); }}
                    className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-500 hover:bg-slate-50 transition-colors"
                    data-testid={`${dataTestId}-custom-toggle`}
                  >
                    Don't see your category? Type it in
                  </button>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCustomSubmit(); }}
                      placeholder="Type your category..."
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      autoFocus
                      data-testid={`${dataTestId}-custom-input`}
                    />
                    <button
                      type="button"
                      onClick={handleCustomSubmit}
                      disabled={!customText.trim()}
                      className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`${dataTestId}-custom-submit`}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
